import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Pin, PinOff } from 'lucide-react'

import { Button, Can, EmptyState, ErrorState, Field, Loading, Pill, SectionHeader, inputClass } from '@/components/ui'
import { Permissions } from '@/lib/session'
import type { Project } from '@/lib/domain'
import { ragTone, stageLabel } from '@/lib/domain'
import { usePinProject, useRankedProjects } from '@/features/projects/useProjects'
import { InversionBanner } from '@/features/delivery/ProjectDelivery'
import { useInversions } from '@/features/delivery/useDelivery'
import { TeamBar, type TeamSelection } from '@/features/teams/TeamBar'
import { useTeams } from '@/features/teams/useTeams'


/** A project as the ranked endpoint returns it: everything, plus its place in the stack. */
type RankedProject = Project & { rank: number | null }

/** Pin a project above its score, or let it go back.
 *
 * The reason is required by the API and asked for here rather than assumed, because the
 * whole point of a pin is that it overrules the arithmetic everyone else's work was
 * ordered by. "CEO asked for it" is a perfectly good answer; no answer is not.
 */
function PinControl({ project }: { project: RankedProject }) {
  const pin = usePinProject()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const pinned = project.rankOverride !== null

  if (pinned) {
    return (
      <Button
        variant="secondary"
        disabled={pin.isPending}
        title={project.rankOverrideReason || 'Pinned above its score'}
        onClick={() =>
          pin.mutate({ id: project.id, position: null, reason: '', rowVersion: project.rowVersion })
        }
      >
        <PinOff size={14} aria-hidden />
        Unpin
      </Button>
    )
  }

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Pin size={14} aria-hidden />
        Pin
      </Button>
    )
  }

  return (
    <div className="flex w-full flex-wrap items-end gap-2">
      <div className="min-w-[220px] flex-1">
        <Field label="Why is this pinned?">
          <input
            className={inputClass}
            value={reason}
            autoFocus
            placeholder="e.g. CEO priority for the year"
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setOpen(false)
            }}
          />
        </Field>
      </div>
      <Button
        variant="primary"
        disabled={!reason.trim() || pin.isPending}
        onClick={() =>
          pin.mutate(
            // Position 1: pinning is nearly always "this goes to the top". Reordering
            // several pinned projects against each other is a problem nobody has yet.
            { id: project.id, position: 1, reason: reason.trim(), rowVersion: project.rowVersion },
            { onSuccess: () => { setReason(''); setOpen(false) } },
          )
        }
      >
        {pin.isPending ? 'Pinning…' : 'Pin to top'}
      </Button>
      <Button variant="secondary" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      {pin.error ? <div className="w-full"><ErrorState error={pin.error} /></div> : null}
    </div>
  )
}

export function PrioritiesPage() {
  // The team lens lives in the URL, as it does on Projects, so a team lead can bookmark
  // their own stack. Asked for by Ben Wood (feedback, 2026-09-10): a team wants to read and
  // pin priorities among its own projects, not interleaved with the other two teams'.
  // Rank is recomputed within the selection — position 1 is that team's top project.
  const [params, setParams] = useSearchParams()
  const selection: TeamSelection = {
    team: params.get('team') ?? undefined,
    categoryId: params.get('categoryId') ?? undefined,
  }
  const { data: teamData } = useTeams()
  const activeTeam = teamData?.teams.find((team) => team.team === selection.team)
  const { data, isPending, error } = useRankedProjects(selection)
  // The one thing this page can tell you that reading any single project cannot.
  const { data: inversions } = useInversions()
  const projects = data?.projects ?? []
  const unscored = projects.filter((p) => p.rank === null)
  // The five the organisation is actually working on. Pinned projects are already first in
  // `rank_projects`, so this needs no special case — CMMC pinned to 1 appears here even
  // though it scores a 2, which is the entire reason pinning exists.
  const topFive = projects.filter((p) => p.rank !== null).slice(0, 5)

  return (
    <div className="mx-auto max-w-[1100px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Priorities</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Ranked by (Value + Risk) ÷ Effort, scored 1–5 where 5 always means more. Pinned
          projects hold their stated position.
          {activeTeam ? ` Ranked within ${activeTeam.label}.` : ''}
        </p>
      </header>

      <TeamBar
        selection={selection}
        onChange={(next: TeamSelection) => {
          const nextParams = new URLSearchParams()
          if (next.team) nextParams.set('team', next.team)
          if (next.categoryId) nextParams.set('categoryId', next.categoryId)
          setParams(nextParams, { replace: true })
        }}
      />

      <InversionBanner inversions={inversions ?? []} />

      {isPending ? (
        <Loading label="Ranking projects" />
      ) : error ? (
        <ErrorState error={error} />
      ) : projects.length === 0 ? (
        <EmptyState title="Nothing to rank yet">
          Score a project on Value, Risk and Effort and it will appear here.
        </EmptyState>
      ) : (
        <>
          <SectionHeader>{activeTeam ? `${activeTeam.shortLabel} top 5` : 'Top 5'}</SectionHeader>
          <ol className="card mb-8 divide-y divide-border">
            {topFive.map((project) => (
              <li key={project.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <span
                  className="w-8 shrink-0 font-heading text-3xl"
                  style={{ color: 'var(--brand)' }}
                >
                  {project.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-fg">{project.name}</div>
                  <div className="text-xs text-fg-muted">
                    {project.rankOverride !== null
                      ? project.rankOverrideReason || 'Pinned above its score'
                      : `${stageLabel(project.stage)} · ${project.teamShortLabel}`}
                  </div>
                </div>
                {project.rankOverride !== null ? <Pill tone="info">Pinned</Pill> : null}
                {project.ragStatus !== 'none' ? (
                  <Pill tone={ragTone(project.ragStatus)}>{project.ragStatus}</Pill>
                ) : null}
              </li>
            ))}
          </ol>

          <SectionHeader>The stack</SectionHeader>
          <ol className="card divide-y divide-border">
            {projects
              .filter((p) => p.rank !== null)
              .map((project) => (
                <li key={project.id} className="flex items-center gap-4 px-4 py-3">
                  <span className="w-8 shrink-0 font-heading text-2xl text-fg-muted">
                    {project.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-fg">
                      {project.name}
                      {project.rankOverride !== null ? (
                        <span className="ml-2">
                          <Pill tone="info">Pinned</Pill>
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-fg-muted">
                      {stageLabel(project.stage)} · {project.teamShortLabel}
                    </div>
                  </div>
                  {project.ragStatus !== 'none' ? (
                    <Pill tone={ragTone(project.ragStatus)}>{project.ragStatus}</Pill>
                  ) : null}
                  <Can permission={Permissions.prioritiesScore}>
                    <PinControl project={project} />
                  </Can>
                  <div className="w-32 shrink-0 text-right">
                    <div className="font-semibold text-fg">{project.scores.priority}</div>
                    <div className="text-[11px] text-fg-muted">
                      V{project.scores.value ?? '—'} · R{project.scores.risk ?? '—'} · E
                      {project.scores.effort ?? '—'}
                    </div>
                  </div>
                </li>
              ))}
          </ol>

          {unscored.length > 0 && (
            <>
              <div className="mt-8">
                <SectionHeader>Not yet scored</SectionHeader>
              </div>
              {/* Listed separately rather than sorted to the bottom of the ranked list.
                  An unscored project is not low priority; it is unknown. */}
              <ul className="card divide-y divide-border">
                {unscored.map((project) => (
                  <li key={project.id} className="flex items-center justify-between px-4 py-3">
                    <span className="truncate text-fg">{project.name}</span>
                    <span className="text-xs text-fg-muted">
                      {project.teamShortLabel}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}
